import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { resetUserScopedStores } from '@/stores/resetUserScoped'
import { usePipelinesStore } from '@/stores/pipelines'
import { dashboardPathFor } from '@/components/shell/navModel'
import { canOpenPipelineBoard, readLastPipelineId, resolvePipelineHome } from '@/lib/pipelineHome'
import { canOpenEngage } from '@/lib/engageGate'
import { legacyEngageRoutes } from './engageRedirects'
import { companyPaymentsRedirect, paymentsRedirect } from '@/views/products/productsPageModel'

/**
 * True from the Tailwind `lg` breakpoint up: the same 1024px threshold as
 * `useMediaQuery().isDesktop`, which decides between the desktop and mobile Settings layouts.
 */
const isDesktopViewport = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches

/** Where a signed-in user lands from "/" (role-based dashboard). */
function homePathFor(authStore: ReturnType<typeof useAuthStore>): string {
  if (authStore.isRootUser) return '/admin'
  // Role → dashboard rules live in the nav model so the sidebar and the router can't drift.
  return dashboardPathFor(authStore.user?.role)
}

/**
 * Guard for the Engage pages (/inbox, /campaigns): open to admins (email) and to non-readonly users
 * whose org has WhatsApp Outreach on; everyone else goes to the dashboard. A member awaits org
 * hydration because `Organization.whatsapp.enabled` loads in the background after a hard page-load,
 * so reading it straight off the store would bounce a WhatsApp member (same race as the
 * requiresWhatsapp guard).
 */
async function engageGuard() {
  const authStore = useAuthStore()
  // Admins pass whatever the org says (email), so skip the org fetch for them.
  if (authStore.isAdmin) return true
  // Read-only users never pass (the inbox and campaign APIs are Member+): no org fetch either.
  const isReadonly = authStore.user?.role === 'readonly'
  if (isReadonly) return '/dashboard'
  const org = await authStore.ensureOrganizationLoaded()
  return canOpenEngage({ isAdmin: false, isReadonly, whatsappEnabled: !!org?.whatsapp?.enabled }) ? true : '/dashboard'
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/landing',
      // "/" is the public home page: logged-out visitors (and crawlers) get the landing
      // page there, with no redirect. Signed-in users are sent on in the guard below.
      // Registered before the app layout's "/" so it wins the match.
      alias: '/',
      name: 'Landing',
      component: () => import('@/views/LandingView.vue'),
      meta: { requiresGuest: true, prerender: true, public: true }
    },
    // Marketing pages — public, prerendered at build, free of auth/subscription guards.
    {
      path: '/pricing',
      name: 'Pricing',
      component: () => import('@/views/marketing/MarkdownView.vue'),
      props: { folder: 'pricing', slug: 'main' },
      meta: { public: true, prerender: true, contentFolder: 'pricing' }
    },
    {
      path: '/use-cases/:slug',
      name: 'UseCase',
      component: () => import('@/views/marketing/MarkdownView.vue'),
      meta: { public: true, prerender: true, contentFolder: 'use-cases' }
    },
    {
      path: '/compare/:slug',
      name: 'Compare',
      component: () => import('@/views/marketing/MarkdownView.vue'),
      meta: { public: true, prerender: true, contentFolder: 'compare' }
    },
    {
      path: '/features/:slug',
      name: 'Feature',
      component: () => import('@/views/marketing/MarkdownView.vue'),
      meta: { public: true, prerender: true, contentFolder: 'features' }
    },
    {
      path: '/blog',
      name: 'BlogIndex',
      component: () => import('@/views/marketing/BlogIndexView.vue'),
      meta: { public: true, prerender: true }
    },
    {
      path: '/blog/:slug',
      name: 'BlogPost',
      component: () => import('@/views/marketing/MarkdownView.vue'),
      meta: { public: true, prerender: true, contentFolder: 'blog' }
    },
    {
      path: '/help',
      name: 'HelpIndex',
      component: () => import('@/views/marketing/HelpIndexView.vue'),
      meta: { public: true, prerender: true }
    },
    {
      path: '/help/:slug',
      name: 'HelpArticle',
      component: () => import('@/views/marketing/MarkdownView.vue'),
      meta: { public: true, prerender: true, contentFolder: 'help' }
    },
    {
      path: '/demo',
      name: 'Demo',
      component: () => import('@/views/marketing/DemoView.vue'),
      meta: { public: true }
    },
    {
      path: '/newsletter/confirm',
      name: 'NewsletterConfirm',
      component: () => import('@/views/marketing/NewsletterActionView.vue'),
      props: { mode: 'confirm' },
      meta: { public: true }
    },
    {
      path: '/newsletter/unsubscribe',
      name: 'NewsletterUnsubscribe',
      component: () => import('@/views/marketing/NewsletterActionView.vue'),
      props: { mode: 'unsubscribe' },
      meta: { public: true }
    },
    {
      path: '/outreach/u/:token',
      name: 'OutreachUnsubscribe',
      component: () => import('@/views/outreach/OutreachUnsubscribeView.vue'),
      meta: { public: true },
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/signup',
      name: 'Signup',
      component: () => import('@/views/auth/SignupView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/locked-out',
      name: 'LockedOut',
      component: () => import('@/views/auth/LockedOutView.vue'),
      meta: { requiresAuth: true }
    },
    // Admin routes (root user only)
    {
      path: '/admin',
      component: () => import('@/views/admin/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresRoot: true },
      children: [
        {
          path: '',
          name: 'AdminDashboard',
          component: () => import('@/views/admin/AdminDashboard.vue')
        },
        {
          path: 'organizations',
          name: 'AdminOrganizations',
          component: () => import('@/views/admin/AdminOrganizations.vue')
        },
        {
          path: 'payment-logs',
          name: 'AdminPaymentLogs',
          component: () => import('@/views/admin/PaymentLogs.vue')
        }
      ]
    },
    {
      path: '/',
      component: () => import('@/layouts/MainLayout.vue'),
      meta: { requiresAuth: true, requiresRegularUser: true },
      children: [
        {
          path: '',
          redirect: () => homePathFor(useAuthStore()),
        },
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: () => import('@/views/DashboardView.vue')
        },
        {
          path: 'sales-dashboard',
          name: 'SalesDashboard',
          component: () => import('@/views/SalesDashboardView.vue')
        },
        {
          path: 'kam-dashboard',
          name: 'KamDashboard',
          component: () => import('@/views/KamDashboardView.vue')
        },
        {
          path: 'profile',
          name: 'Profile',
          component: () => import('@/views/ProfileView.vue')
        },
        {
          path: 'deals',
          name: 'DealsList',
          component: () => import('@/views/deals/DealsList.vue'),
          // The page syncs its view and filters with the query itself (watching route.query), so a
          // query change must not remount it (MainLayout keys the router-view on the path then).
          meta: { keyOnPath: true }
        },
        {
          path: 'deals/hot',
          name: 'HotDeals',
          component: () => import('@/views/deals/HotDealsView.vue'),
          meta: { keyOnPath: true }
        },
        {
          path: 'deals/:id',
          name: 'DealDetails',
          component: () => import('@/views/deals/DealDetails.vue'),
          // `?activityId=` (reminders) opens the activity window and is then taken out of the URL;
          // the page keeps its instance across that query change.
          meta: { keyOnPath: true }
        },
        {
          path: 'contacts',
          name: 'ContactsList',
          component: () => import('@/views/contacts/ContactsList.vue'),
          meta: { keyOnPath: true }
        },
        {
          path: 'contacts/:id',
          name: 'ContactDetail',
          component: () => import('@/views/contacts/ContactDetails.vue'),
          // `?activityId=` (reminders) opens the activity window and is then taken out of the URL;
          // the page keeps its instance across that query change.
          meta: { keyOnPath: true }
        },
        {
          path: 'companies',
          name: 'CompaniesList',
          component: () => import('@/views/companies/CompaniesList.vue'),
          // The page keeps its segment and tag filter in the query and applies query changes itself.
          meta: { keyOnPath: true }
        },
        {
          path: 'companies/:id',
          name: 'CompanyDetail',
          component: () => import('@/views/companies/CompanyDetails.vue'),
          // The page keeps its tab in the query (?tab=payments) and applies query changes itself, so
          // switching Overview / Payments keeps the instance and what it has loaded.
          meta: { keyOnPath: true }
        },
        {
          path: 'activities',
          name: 'Activities',
          component: () => import('@/views/ActivitiesList.vue'),
          meta: { keyOnPath: true }
        },
        {
          path: 'deal-cadence',
          name: 'DealCadence',
          component: () => import('@/views/DealCadenceView.vue'),
          meta: { keyOnPath: true }
        },
        {
          path: 'notes',
          name: 'NotesList',
          component: () => import('@/views/NotesListView.vue'),
          meta: { requiresAdmin: true, keyOnPath: true },
        },
        {
          // No requiresRecordings here on purpose: an unentitled org must still reach this
          // page so it sees the usage meter and the upgrade prompt. A nav entry that simply
          // vanishes tells the user nothing and sells nothing.
          path: 'recordings',
          name: 'RecordingsList',
          component: () => import('@/views/recordings/RecordingsList.vue'),
          meta: { requiresAdmin: true, keyOnPath: true },
        },
        {
          path: 'recordings/uploader-test',
          name: 'RecordingsUploaderTest',
          component: () => import('@/views/recordings/RecordingsUploaderTestView.vue'),
          meta: { requiresAdmin: true, requiresRecordings: true },
        },
        {
          // The sidebar's "Pipeline" entry. It has no page: the guard loads the org's pipelines
          // and redirects to the board last viewed (else the default, else the first), or to the
          // list when there are none. The component is only here because a record needs one; it
          // is never resolved, since the redirect happens before components are loaded.
          path: 'pipeline',
          name: 'PipelineHome',
          component: () => import('@/views/pipelines/PipelinesList.vue'),
          beforeEnter: async (to) => {
            const authStore = useAuthStore()
            const go = (path: string) => ({ path, query: to.query, hash: to.hash })
            // Readonly users cannot open a board (the board endpoints answer 403): send them to
            // the list without fetching anything.
            if (!canOpenPipelineBoard(authStore.user?.role)) return go('/pipelines')
            // Same as the requiresWhatsapp guard: the org hydrates in the background after a hard
            // load, so wait for it rather than listing pipelines for a half-initialised session.
            await authStore.ensureOrganizationLoaded()
            const pipelinesStore = usePipelinesStore()
            await pipelinesStore.fetchPipelines()
            return go(resolvePipelineHome(pipelinesStore.pipelines, readLastPipelineId()))
          },
        },
        {
          path: 'pipelines',
          name: 'PipelinesList',
          component: () => import('@/views/pipelines/PipelinesList.vue'),
          // R1: admins manage pipelines in Settings › Pipelines & stages (query kept, so a
          // ?pipeline=<id> or ?org=<slug> link still lands on the right tab and org); everyone else
          // (members, KAMs, read-only) gets the read-only list here.
          beforeEnter: (to) => {
            const authStore = useAuthStore()
            if (authStore.isAdmin) return { path: '/settings/pipelines', query: to.query, hash: to.hash }
            return true
          },
        },
        {
          path: 'pipelines/:id',
          name: 'PipelineView',
          component: () => import('@/views/pipelines/PipelineView.vue')
        },
        {
          // Payments moved into the Products & payments page (D15): old links and bookmarks land on
          // its payments section, Received. A company's old payments page opens that company's
          // Payments tab (/companies/:id?tab=payments, phase 9 R11). Both keep their names so named
          // links resolve; the global guard still runs on the target, so ?org= is kept (or appended).
          path: 'payments',
          name: 'PaymentHistory',
          redirect: (to) => paymentsRedirect(to),
        },
        {
          path: 'payments/company/:companyId',
          name: 'CompanyPaymentHistory',
          redirect: (to) => companyPaymentsRedirect(to),
        },
        {
          path: 'products',
          name: 'ProductsList',
          component: () => import('@/views/ProductsView.vue'),
          // The payments section keeps its list, month and company in the query and applies query
          // changes itself, so a query change must not remount the page.
          meta: { keyOnPath: true }
        },
        {
          path: 'products/:id',
          name: 'ProductDetails',
          component: () => import('@/views/products/ProductDetails.vue')
        },
        {
          // Engage (phase 6): one Inbox and one Campaigns page for both channels. Each page keeps
          // its channel / status / thread in the query and applies query changes itself, so a query
          // change must not remount it (MainLayout keys the router-view on the path then).
          path: 'inbox',
          name: 'Inbox',
          component: () => import('@/views/inbox/InboxView.vue'),
          meta: { keyOnPath: true },
          beforeEnter: engageGuard,
        },
        {
          path: 'campaigns',
          name: 'Campaigns',
          component: () => import('@/views/campaigns/CampaignsList.vue'),
          meta: { keyOnPath: true },
          beforeEnter: engageGuard,
        },
        // The old per-channel lists and inboxes (/outreach, /outreach/inbox, /whatsapp,
        // /whatsapp/inbox) redirect into Campaigns and the Inbox, query and ?org= kept (R23). The
        // names stay so named links keep resolving; the target's engageGuard decides who may open it.
        ...legacyEngageRoutes,
        {
          path: 'outreach/campaigns/:id',
          name: 'OutreachCampaignBuilder',
          component: () => import('@/views/outreach/OutreachCampaignBuilderView.vue'),
          meta: { requiresAuth: true, requiresAdmin: true },
        },
        {
          path: 'whatsapp/campaigns/:id',
          name: 'WaCampaignBuilder',
          component: () => import('@/views/whatsapp/WaCampaignBuilderView.vue'),
          // requiresWhatsapp gates on Organization.whatsapp.enabled (Settings → WhatsApp itself
          // is NOT gated by it — an admin must be able to reach Settings to turn it on).
          // Not admin-gated: the backend scopes non-admins to campaigns they created.
          meta: { requiresAuth: true, requiresWhatsapp: true },
        },
        {
          path: 'notifications',
          name: 'Notifications',
          component: () => import('@/views/NotificationsView.vue')
        },
        {
          path: 'more',
          name: 'More',
          component: () => import('@/views/MobileMoreView.vue')
        },
        {
          path: 'analytics',
          component: () => import('@/views/analytics/AnalyticsLayout.vue'),
          meta: { requiresAdmin: true },
          children: [
            {
              path: '',
              redirect: '/analytics/sales'
            },
            {
              path: 'sales',
              name: 'AnalyticsSales',
              component: () => import('@/views/analytics/SalesReportView.vue')
            },
            {
              path: 'kam',
              name: 'AnalyticsKam',
              component: () => import('@/views/analytics/KamReportView.vue')
            },
            {
              path: 'smart',
              name: 'AnalyticsSmart',
              component: () => import('@/views/analytics/SmartReportView.vue')
            },
            {
              path: 'lead-quality',
              name: 'AnalyticsLeadQuality',
              component: () => import('@/views/analytics/LeadQualityView.vue')
            },
            {
              // The only Analytics tab a non-admin can open. Child meta wins over the
              // parent's requiresAdmin, so KAMs and sales executives reach their own day
              // here; the backend scopes them to themselves either way.
              path: 'timeline',
              name: 'AnalyticsDailyTimeline',
              meta: { requiresAdmin: false },
              component: () => import('@/views/analytics/DailyTimelineView.vue')
            },
          ]
        },
        {
          path: 'settings',
          component: () => import('@/views/SettingsLayout.vue'),
          // Admin+ can enter Settings. Owner-only sub-pages (Team, Organization,
          // Billing, Outreach, Contact us, Audit log) re-assert requiresOwner below;
          // admins reach Pipelines & stages, API keys, WhatsApp, Call analysis and
          // Data cleanup.
          meta: { requiresAdmin: true },
          children: [
            {
              path: '',
              // Named so a help article can attach to the phone section list (SettingsHome).
              name: 'SettingsHome',
              // Below the desktop breakpoint /settings IS the page: SettingsLayout renders the
              // grouped section list for it, so this index child renders nothing itself. From
              // the desktop breakpoint up it passes through to the default Settings tab (API
              // Keys, which the global guard upgrades to Organization for owners, see below).
              // A guard rather than a `redirect` record because a record redirect can't be
              // conditional. It forwards the query so a /settings?org=<slug> deep-link keeps
              // its org param, and a guard redirect keeps `redirectedFrom` = /settings, which
              // is what the owner upgrade keys off.
              component: { render: () => null },
              beforeEnter: (to) =>
                isDesktopViewport() ? { path: '/settings/api-keys', query: to.query } : true,
            },
            {
              path: 'users',
              name: 'SettingsUsers',
              meta: { requiresOwner: true },
              component: () => import('@/views/settings/UsersView.vue')
            },
            {
              path: 'organization',
              name: 'SettingsOrganization',
              meta: { requiresOwner: true },
              component: () => import('@/views/settings/OrganizationView.vue')
            },
            {
              path: 'billing',
              name: 'SettingsBilling',
              meta: { requiresOwner: true },
              component: () => import('@/views/settings/BillingView.vue')
            },
            {
              // Admin+ (inherits the parent's requiresAdmin): the pipeline list and stage editor
              // moved here from /pipelines, matching the backend's admin-level pipeline writes.
              path: 'pipelines',
              name: 'SettingsPipelines',
              // The page switches pipeline tabs through ?pipeline=<id> itself: a query change must
              // not remount it (MainLayout keys the router-view on the path then).
              meta: { keyOnPath: true },
              component: () => import('@/views/settings/pipelines/SettingsPipelinesView.vue')
            },
            {
              path: 'contact-us',
              name: 'SettingsContactUs',
              meta: { requiresOwner: true },
              component: () => import('@/views/settings/ContactUsView.vue')
            },
            {
              // Admin+ (inherits the parent's requiresAdmin): integration and recorder agent
              // keys. Also the default desktop Settings tab for admins (owners go to Organization).
              path: 'api-keys',
              name: 'SettingsApiKeys',
              component: () => import('@/views/settings/ApiKeysView.vue')
            },
            {
              path: 'audit-logs',
              name: 'SettingsAuditLogs',
              meta: { requiresOwner: true },
              component: () => import('@/views/settings/AuditLogsView.vue')
            },
            {
              path: 'outreach',
              name: 'SettingsOutreach',
              meta: { requiresOwner: true },
              component: () => import('@/views/settings/OutreachSettingsView.vue')
            },
            {
              // Admin+ (matches the backend's @Roles(ADMIN) guard on /whatsapp/* routes) —
              // no requiresOwner override, so it inherits the parent's requiresAdmin.
              path: 'whatsapp',
              name: 'SettingsWhatsapp',
              component: () => import('@/views/settings/WhatsappSettingsView.vue')
            },
            {
              // Admin+, matching the backend's @Roles(ADMIN) guard on /call-analysis/settings.
              path: 'call-analysis',
              name: 'SettingsCallAnalysis',
              component: () => import('@/views/settings/CallAnalysisSettingsView.vue')
            },
            {
              // Admin+, matching the backend's @Roles(ADMIN) guard on /archive-rules —
              // inherits the parent's requiresAdmin, no requiresOwner override.
              path: 'data-cleanup',
              name: 'SettingsDataCleanup',
              component: () => import('@/views/settings/DataCleanupView.vue')
            }
          ]
        }
      ]
    },
    // Catch-all 404. Must be the last route so explicit paths (login, signup,
    // dashboard, etc.) match first. NotFoundRouteView picks the variant: the in-app 404
    // inside MainLayout for signed-in org users, the marketing NotFoundView otherwise.
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/errors/NotFoundRouteView.vue'),
      meta: { public: true }
    }
  ]
})

// ── Org-in-URL helpers ─────────────────────────────────────────────────────
// The active organization is mirrored in the URL as ?org=<slug> on app routes so
// any page can be bookmarked/shared and reopened in that org. Root users (org-
// agnostic, no switcher) and public routes are excluded.

type AuthStore = ReturnType<typeof useAuthStore>

function isOrgScopedRoute(authStore: AuthStore, to: RouteLocationNormalized): boolean {
  return (
    authStore.isAuthenticated &&
    !authStore.isRootUser &&
    !!to.meta.requiresAuth &&
    !to.meta.public
  )
}

/** Resolve a ?org value (slug, or org id as a fallback) to one of the user's memberships. */
function findMembershipByParam(authStore: AuthStore, orgParam: string) {
  return authStore.memberships.find((m) => m.slug === orgParam || m.orgId === orgParam)
}

/** Same route, with ?org set to `slug` (or the param stripped if there's no active org). */
function withOrgParam(to: RouteLocationNormalized, slug?: string) {
  const query = { ...to.query }
  if (slug) query.org = slug
  else delete query.org
  return { path: to.path, query, hash: to.hash }
}

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  if (authStore.isAuthenticated && !authStore.user) {
    try {
      await authStore.initializeAuth()
    } catch (e) {
      // If hydration fails, fall through to normal checks
    }
  }

  const orgScoped = isOrgScopedRoute(authStore, to)

  // Active org ⇄ URL (?org=<slug>), part 1: adopt the URL's org. On app routes the
  // URL is the source of truth for the active org. If ?org names a DIFFERENT org
  // the user can access, switch into it now — before the owner-upgrade and the
  // role checks below, which both depend on the per-org role. An unknown /
  // no-access slug degrades to the current org with a toast.
  if (orgScoped) {
    // The slug⇄org map lives in memberships (loaded non-blocking at boot); make
    // sure it's present before resolving a param or reading the active slug.
    if (!authStore.memberships.length) {
      try {
        await authStore.fetchMyOrganizations()
      } catch {
        // non-fatal — switcher just won't populate
      }
    }
    const orgParam = typeof to.query.org === 'string' ? to.query.org : undefined
    if (orgParam && orgParam !== authStore.activeOrg?.slug) {
      const target = findMembershipByParam(authStore, orgParam)
      if (target) {
        await authStore.switchOrg(target.orgId)
        resetUserScopedStores()
        authStore.bumpOrgView()
        // activeOrg now matches the URL — fall through.
      } else {
        try {
          useToast().error(`No access to organization "${orgParam}".`)
        } catch {
          // toast plugin not ready on the very first navigation — skip silently
        }
        return next(withOrgParam(to, authStore.activeOrg?.slug))
      }
    }
  }

  // Settings default tab (role-aware). On desktop the /settings index passes everyone
  // through to API Keys (its beforeEnter reads no auth state, so the target is the same for
  // every role); now that auth is resolved (and any ?org switch above applied the right
  // role), send owners to their Organization tab instead. Keyed off redirectedFrom so a
  // deliberate API Keys visit is never hijacked; the query (incl. ?org) is kept. Below the
  // desktop breakpoint /settings is not redirected at all: it shows the section list.
  if (
    authStore.isOwner &&
    to.path === '/settings/api-keys' &&
    to.redirectedFrom?.path === '/settings'
  ) {
    return next({ path: '/settings/organization', query: to.query })
  }

  // Block all navigation when subscription-locked (user can only logout via overlay).
  // Public marketing routes (meta.public) stay reachable so the user can read pricing/help while resolving billing.
  if (
    authStore.subscriptionLockedOut &&
    to.name !== 'Login' &&
    to.name !== 'Landing' &&
    !to.meta.public
  ) {
    return next(false)
  }

  // Active org ⇄ URL, part 2: keep the param in the URL. If an app route has no
  // ?org, append the active org's slug so the page is bookmarkable. Runs AFTER the
  // owner-upgrade so this redirect doesn't consume the redirectedFrom signal that
  // upgrade relies on.
  if (orgScoped) {
    const orgParam = typeof to.query.org === 'string' ? to.query.org : undefined
    const activeSlug = authStore.activeOrg?.slug
    if (!orgParam && activeSlug) {
      return next(withOrgParam(to, activeSlug))
    }
  }

  if (to.path === '/' && authStore.isAuthenticated) {
    // The public home page doubles as the app entry: signed-in users go to their dashboard.
    next(homePathFor(authStore))
  } else if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/')
  } else if (to.meta.requiresRoot && !authStore.isRootUser) {
    // Redirect non-root users away from admin pages
    next('/')
  } else if (to.meta.requiresAdmin && !authStore.isAdmin) {
    // Redirect non-admin users away from admin-only pages
    next('/dashboard')
  } else if (to.meta.requiresWhatsapp && !(await authStore.ensureOrganizationLoaded())?.whatsapp?.enabled) {
    // Redirect away when the org hasn't turned WhatsApp Outreach on (Organization.whatsapp.enabled).
    // Awaits org hydration — initializeAuth loads the org in the background, so reading
    // authStore.organization directly here races to null on a hard page-load and wrongly bounces.
    next('/dashboard')
  } else if (to.meta.requiresRecordings && !(await authStore.ensureOrganizationLoaded())?.recordings?.enabled) {
    // Only the agent-setup helper still hard-redirects. Send it to /recordings (which shows the
    // meter + upgrade prompt) rather than the dashboard, so the user learns why.
    next('/recordings')
  } else if (to.meta.requiresOwner && !authStore.isOwner) {
    // Redirect non-owner users away from settings pages
    next('/dashboard')
  } else if (to.meta.requiresGuest && authStore.isAuthenticated) {
    // Root user should go to admin dashboard, regular users to dashboard
    next(authStore.isRootUser ? '/admin' : '/dashboard')
  } else {
    next()
  }
})

// Friendly per-route document.title. Marketing routes keep their prerendered
// titles (we don't override when meta.prerender is set). Anything else gets
// "<section> · NerdCRM" so the browser tab tells you where you are.
const ROUTE_TITLES: Record<string, string> = {
  Dashboard: 'Today', // the admin dashboard is the Workbench Today page
  SalesDashboard: 'Today',
  KamDashboard: 'Today',
  AdminDashboard: 'Overview — Admin',
  AdminOrganizations: 'Organizations — Admin',
  AdminPaymentLogs: 'Payment logs — Admin',
  Profile: 'My Profile',
  DealsList: 'Deals',
  HotDeals: 'Hot Deals',
  DealDetails: 'Deal Details',
  ContactsList: 'Contacts',
  ContactDetail: 'Contact Details',
  CompaniesList: 'Companies',
  CompanyDetail: 'Company Details',
  Activities: 'Activities',
  NotesList: 'Notes',
  RecordingsList: 'Call Recordings',
  RecordingsUploaderTest: 'Recordings Uploader (test)',
  PipelineHome: 'Pipeline',
  PipelinesList: 'Pipelines',
  PipelineView: 'Pipeline',
  ProductsList: 'Products & payments',
  ProductDetails: 'Product Details',
  OutreachCampaignBuilder: 'Campaign',
  Inbox: 'Inbox',
  Campaigns: 'Campaigns',
  Notifications: 'Notifications',
  More: 'More',
  AnalyticsSales: 'Sales Analytics',
  AnalyticsKam: 'KAM Analytics',
  AnalyticsLeadQuality: 'Lead Quality',
  AnalyticsDailyTimeline: 'Daily timeline',
  AnalyticsSmart: 'Ask AI',
  SettingsHome: 'Settings',
  SettingsCallAnalysis: 'Call Analysis',
  SettingsPipelines: 'Pipelines & stages — Settings',
  SettingsUsers: 'Team & roles — Settings',
  SettingsOrganization: 'Organization — Settings',
  SettingsBilling: 'Billing & seats — Settings',
  SettingsContactUs: 'Contact support — Settings',
  SettingsApiKeys: 'API Keys — Settings',
  SettingsAuditLogs: 'Audit log — Settings',
  SettingsOutreach: 'Email outreach — Settings',
  Login: 'Sign In',
  Signup: 'Sign Up',
  ResetPassword: 'Reset Password',
  LockedOut: 'Account Locked',
  NotFound: 'Page Not Found',
}

// Track every successful SPA navigation as a pageview. Lazy-loaded so the
// analytics composable isn't pulled into the marketing prerender bundle.
router.afterEach(async (to) => {
  if (typeof window === 'undefined') return
  if ((window as { __PRERENDER_INJECTED?: { isPrerender?: boolean } }).__PRERENDER_INJECTED?.isPrerender) return

  // Marketing pages set their own titles via prerendered HTML / MarkdownView head metadata.
  if (!to.meta.prerender && to.name) {
    const label = ROUTE_TITLES[String(to.name)]
    if (label) document.title = `${label} · NerdCRM`
  }

  try {
    const { useAnalytics } = await import('@/composables/useAnalytics')
    useAnalytics().trackPageView(to.fullPath)
  } catch {
    // ignore
  }
})

export default router
